import { WebSocketStore } from 'stores/WebSocketStore';
import { getAccessToken } from 'utils/AuthUtils/tokenUtils';

/**
 * Get WebSocket URL based on backend configuration
 * Always uses VITE_BACKEND_SOCKET_ENDPOINT if available
 */
function getWebSocketUrl(): string {
  let wsBaseUrl: string;

  if (import.meta.env.VITE_MODE == 'dev') {
    wsBaseUrl = import.meta.env.VITE_SOCKET_BASE_URL;
  } else {
    wsBaseUrl = (window as any).config?.socketBaseUrl;
  }

  // If no WebSocket URL is configured, handle gracefully and prevent connection
  if (!wsBaseUrl) {
    console.error(
      'WebSocket URL not found in the configuration. WebSocket connection will not be established.'
    );
    throw new Error('WebSocket URL not found');
  }

  // Get token from localStorage
  const token = getAccessToken();

  if (!token) {
    throw new Error('No access token found. User must be authenticated.');
  }

  // Build WebSocket URL with token in query string
  const wsUrl = `${wsBaseUrl}/ws/notifications/?token=${encodeURIComponent(token)}`;

  return wsUrl;
}

/**
 * Initialize WebSocket connection
 */
export function initializeWebSocket(webSocketStore: WebSocketStore): void {
  try {
    const wsUrl = getWebSocketUrl();
    webSocketStore.connect(wsUrl);
  } catch (error: any) {
    console.error('Failed to initialize WebSocket:', error);
    // Store will handle the error state
  }
}

/**
 * Set up heartbeat response handler
 */
export function setupHeartbeatHandler(webSocketStore: WebSocketStore): void {
  webSocketStore.on('heartbeat', (_data: any) => {
    // Respond to heartbeat to keep connection alive
    webSocketStore.sendMessage({ type: 'heartbeat_response' });
  });
}

/**
 * Set up notification handler
 */
export function setupNotificationHandler(
  webSocketStore: WebSocketStore,
  onNotification?: (notification: any) => void
): void {
  webSocketStore.on('notification', (data: any) => {
    if (data?.data) {
      // Call custom handler if provided
      if (onNotification) {
        onNotification(data.data);
      }
      // Emit notification event for other components to listen
      webSocketStore.emit('notification_received', data.data);
    }
  });
}

/**
 * Clean up WebSocket connection
 */
export function cleanupWebSocket(webSocketStore: WebSocketStore): void {
  webSocketStore.disconnect();
}
